/**
 * Dialog strategies for the popup system. Each knows one way to put a dialog on
 * screen and take it off, and owns what follows: whether the page is blocked,
 * whether focus is taken, who handles escape. Popup.createDialogStrategy() picks one
 * from what Liquid rendered, so Popup never branches on presentation.
 *
 * Two axes: presentation (modal blocks the page, corner card leaves it alone) and
 * capability (the Fallback classes stand in where <dialog> is missing). Each pairing
 * is its own class; a new presentation is two classes here and a branch in the factory.
 */

window.UFT = window.UFT || {};
window.UFT.PopupStrategies =
	window.UFT.PopupStrategies ||
	(function () {
		const SELECTORS = {
			CONTENT: '.popup__content',
			BUTTON: 'button',
			POPUP_ANNOUNCER: '.popups__announcer',
			HEADING: '.popup__heading',
			// The message popup's body copy, not the age gate's decline-notice sink.
			DESCRIPTION: '.popup__description',
			CTA: '.popup__cta',
		};
		const CSS_CLASSES = { FALLBACK_OPEN: 'popup--fallback-open' };
		// Mirrors DrawerContent.FOCUSABLE_SELECTOR in assets/drawer.js, copied so the popups do
		// not depend on the drawer. a[href], not [href]: a bare [href] matches <link> and <base>.
		const FOCUSABLE_SELECTOR = [
			'a[href]:not([tabindex="-1"])',
			'button:not([disabled]):not([tabindex="-1"])',
			'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
			'select:not([disabled]):not([tabindex="-1"])',
			'textarea:not([disabled]):not([tabindex="-1"])',
			'[tabindex]:not([tabindex="-1"])',
		].join(', ');
		const KEYS = { ESCAPE: 'Escape' };
		const FOCUS_DELAY_MS = 100;

		// A card takes no focus and has no backdrop, so the live region is the only arrival
		// signal for assistive tech. It sits outside every dialog so it is already in the tree.
		function announceCardArrival(dialog) {
			const announcer = document.querySelector(SELECTORS.POPUP_ANNOUNCER);
			const label = cardArrivalLabel(dialog);
			if (!announcer || !label) {
				return;
			}

			// Cleared and refilled in a separate task: identical text is commonly not re-announced.
			announcer.textContent = '';
			setTimeout(() => {
				announcer.textContent = label;
			}, 0);
		}

		// The prefix carries what the visuals do: that something arrived.
		function cardArrivalLabel(dialog) {
			const { HEADING, DESCRIPTION, CTA } = SELECTORS;
			const heading = dialog.querySelector(HEADING)?.textContent.trim();
			const body = dialog.querySelector(DESCRIPTION)?.textContent.trim();
			const cta = dialog.querySelector(CTA)?.textContent.trim();
			const text = heading || body || cta;

			return text ? `Notification: ${text}` : '';
		}

		// document.activeElement reports a shadow HOST, not the control inside it, and a host with
		// no tabindex refuses focus(). Mirrors DrawerContent.deepActiveElement, copied for the
		// same reason FOCUSABLE_SELECTOR is. A closed shadow root stays opaque.
		function deepActiveElement() {
			let active = document.activeElement;
			while (active?.shadowRoot?.activeElement) {
				active = active.shadowRoot.activeElement;
			}

			return active;
		}

		// focus() is a silent no-op on a control CSS has hidden, so the browser is asked rather
		// than a selector; this also covers Safari releases without checkVisibility.
		function focusFirstFocusable(root) {
			const candidates = root.querySelectorAll(FOCUSABLE_SELECTOR);
			for (const candidate of candidates) {
				candidate.focus();
				if (document.activeElement === candidate) {
					return candidate;
				}
			}

			return null;
		}

		class DialogStrategy {
			constructor() {
				this.popup = null;
				this.dialog = null;
				this.announcedArrival = false;
				// Where a visitor who closes a card from inside it is put back.
				this.lastFocusOutside = null;
				this.handleBackdropClick = this.handleBackdropClick.bind(this);
				this.handleFocusOutside = this.handleFocusOutside.bind(this);
			}

			show() {
				throw new Error('show() must be implemented');
			}

			close() {
				throw new Error('close() must be implemented');
			}

			isOpen() {
				throw new Error('isOpen() must be implemented');
			}

			// Whether the page's accessibility drawer must move inside this dialog. A modal is in
			// the top layer (or emulates it), where no z-index reaches. A card shares the page, and
			// reparenting a connected custom element disconnects and closes it.
			hostsAccessibilityDrawer() {
				throw new Error('hostsAccessibilityDrawer() must be implemented');
			}

			// No-op except for the fallback modal: the manager sends the accessibility controls
			// home while the dialog is still open, and a live trap would pull focus back.
			releaseFocusTrap() {}

			// Once per page view: a card that steps aside for a drawer and returns is the same
			// notification, and would otherwise be read out on every cart open.
			announceArrivalOnce() {
				if (this.announcedArrival) {
					return;
				}

				this.announcedArrival = true;
				announceCardArrival(this.dialog);
			}

			// Cards only. A card takes no focus, so a spec close returns the visitor to the card's
			// opener, <body> on an untouched page, losing their place (WCAG 2.4.3).
			trackFocusOutside() {
				document.addEventListener('focusin', this.handleFocusOutside);
			}

			untrackFocusOutside() {
				document.removeEventListener('focusin', this.handleFocusOutside);
			}

			// Only when the close dropped focus. Focus still inside the closed dialog counts: a
			// control that went unfocusable stays activeElement for two frames before falling to <body>.
			returnFocusBesideIfDropped() {
				const active = document.activeElement;
				if (active !== document.body && !this.dialog.contains(active)) {
					return;
				}

				if (!this.lastFocusOutside?.isConnected) {
					return;
				}

				this.lastFocusOutside.focus?.();
			}

			setup(dialog, popup) {
				this.dialog = dialog;
				this.popup = popup;
			}

			cleanup() {
				this.popup = null;
				this.dialog = null;
				this.lastFocusOutside = null;
			}

			// The dialog element fills the viewport, so a click on it rather than its content is outside.
			handleBackdropClick(event) {
				if (event.target === this.dialog && !this.popup.requiresAction()) {
					this.popup.close();
				}
			}

			// Scoped to the content because the manager reparents the accessibility toggle into the
			// dialog. The dialog itself is the fallback: a message popup can have no button, and
			// focus must still land inside a blocking popup.
			focusFirstButtonAfterDelay() {
				const content = this.dialog.querySelector(SELECTORS.CONTENT);
				const target = content?.querySelector(SELECTORS.BUTTON) ?? this.dialog;

				// A button can be withdrawn during the delay (a decline with a redirect hides them all),
				// and focus() on one is a silent no-op. First button, not first focusable: a link must not win.
				setTimeout(() => {
					target.focus();
					if (document.activeElement !== target) {
						this.dialog?.focus();
					}
				}, FOCUS_DELAY_MS);
			}

			// <body> is never recorded: focusin fires at <body> whenever focus is lost, and focusing it
			// is the no-op this exists to avoid. Read off the document, not the event, because focusin
			// retargets a shadow control to its host.
			handleFocusOutside() {
				const focused = deepActiveElement();

				if (!focused || focused === document.body || this.dialog?.contains(focused)) {
					return;
				}

				this.lastFocusOutside = focused;
			}

			mayCloseOnEscape(event) {
				return Boolean(this.dialog) && !event.defaultPrevented && this.isOpen() && !this.popup.requiresAction();
			}

			// An escape another overlay claims (drawer.js marks its own) is not the card's. Decided
			// after dispatch so listener order does not matter; the strategy may be cleaned up by
			// then, hence the dialog check.
			closeUnlessEscapeClaimed(event) {
				if (event.key !== KEYS.ESCAPE) {
					return;
				}

				setTimeout(() => {
					if (!this.mayCloseOnEscape(event)) {
						return;
					}

					this.popup.close();
				}, 0);
			}
		}

		/** Centered over a backdrop, blocking the page. The age gate's default. */
		class ModalDialogStrategy extends DialogStrategy {
			constructor() {
				super();
				this.handleCancel = this.handleCancel.bind(this);
			}

			show() {
				this.dialog.showModal();
				this.dialog.addEventListener('click', this.handleBackdropClick);
				this.dialog.addEventListener('cancel', this.handleCancel);
				this.focusFirstButtonAfterDelay();
			}

			close() {
				this.removeDialogListeners();
				this.dialog.close();
			}

			isOpen() {
				return this.dialog.open;
			}

			hostsAccessibilityDrawer() {
				return true;
			}

			// The browser closes a modal on escape, and on an Android back gesture, without the
			// manager seeing it, stranding the relocated accessibility toggle. Closing through the
			// popup keeps one dismissal path.
			handleCancel(event) {
				event.preventDefault();

				if (this.popup.requiresAction()) {
					return;
				}

				this.popup.close();
			}

			cleanup() {
				this.removeDialogListeners();
				super.cleanup();
			}

			removeDialogListeners() {
				this.dialog?.removeEventListener('click', this.handleBackdropClick);
				this.dialog?.removeEventListener('cancel', this.handleCancel);
			}
		}

		/** Card in a viewport corner over a page that stays usable. For content to notice, not obey. */
		class CornerCardDialogStrategy extends DialogStrategy {
			constructor() {
				super();
				this.handleKeydown = this.handleKeydown.bind(this);
			}

			// show() runs showModal()'s focusing steps, so focus is handed back to keep the card
			// non-modal, and the restore seeds the outside-focus record. Verified because focus() is
			// a no-op on <body>, the opener on an untouched page; the blur clears a ring the visitor
			// never chose. The sequential navigation point stays put, so Tab still enters the card.
			show() {
				const focusedBeforeOpen = deepActiveElement();
				this.dialog.show();
				this.trackFocusOutside();
				document.addEventListener('keydown', this.handleKeydown);
				focusedBeforeOpen?.focus?.();
				this.releaseFocusTakenOnOpen();
				this.announceArrivalOnce();
			}

			// document.activeElement, not deepActiveElement(): contains() does not cross a shadow
			// boundary, and the host is the node to test and blur.
			releaseFocusTakenOnOpen() {
				if (!this.dialog.contains(document.activeElement)) {
					return;
				}

				document.activeElement.blur();
			}

			close() {
				document.removeEventListener('keydown', this.handleKeydown);
				this.untrackFocusOutside();
				this.dialog.close();
				this.returnFocusBesideIfDropped();
			}

			isOpen() {
				return this.dialog.open;
			}

			hostsAccessibilityDrawer() {
				return false;
			}

			// A non-modal dialog is not in the top layer, so the browser supplies no escape handling.
			handleKeydown(event) {
				this.closeUnlessEscapeClaimed(event);
			}

			cleanup() {
				document.removeEventListener('keydown', this.handleKeydown);
				this.untrackFocusOutside();
				super.cleanup();
			}
		}

		/**
		 * Class-and-listener emulation for browsers with no <dialog>. Abstract: arrival,
		 * modality and focus differ per presentation.
		 */
		class FallbackDialogStrategy extends DialogStrategy {
			constructor() {
				super();
				this.focusedBeforeOpen = null;
				this.handleKeydown = this.handleKeydown.bind(this);
			}

			show() {
				// Nothing restores focus on close here as dialog.close() does natively, so the opener is
				// recorded. Resolved through open shadow roots: a host with no tabindex refuses focus().
				this.focusedBeforeOpen = deepActiveElement();
				this.addFallbackOpenClass();
				this.addEventListeners();
				this.signalArrival();
			}

			close() {
				// Asked before the open class goes: removing it is what drops focus to <body>.
				const heldFocus = this.holdsFocus();

				this.removeFallbackOpenClass();
				this.removeEventListeners();

				if (heldFocus || this.restoresFocusUnconditionally()) {
					this.restoreFocusToOpener();
				}

				this.focusedBeforeOpen = null;
				this.dialog.dispatchEvent(new Event('close'));
			}

			isOpen() {
				return this.dialog.classList.contains(CSS_CLASSES.FALLBACK_OPEN);
			}

			setup(dialog, popup) {
				super.setup(dialog, popup);
				this.applyAccessibilityAttributes();
			}

			cleanup() {
				this.removeEventListeners();
				super.cleanup();
			}

			// Arrival differs per presentation: a modal takes focus, a card announces.
			signalArrival() {
				throw new Error('signalArrival() must be implemented');
			}

			restoresFocusUnconditionally() {
				throw new Error('restoresFocusUnconditionally() must be implemented');
			}

			holdsFocus() {
				return this.dialog.contains(this.dialog.ownerDocument.activeElement);
			}

			// Removing the open class drops focus to <body>, the top of the document (WCAG 2.4.3).
			restoreFocusToOpener() {
				const opener = this.focusedBeforeOpen;

				if (opener?.isConnected) {
					opener.focus?.();
				}
			}

			// Applied once and left in place: they describe what the element is, not whether it is
			// open, and the announcer reads the closed dialog's name on arrival. drawer.js reads
			// aria-modal to yield focus, but a closed dialog is display:none, so nothing inside it
			// can hold focus for that question to arise.
			applyAccessibilityAttributes() {
				this.dialog.setAttribute('role', 'dialog');
			}

			addFallbackOpenClass() {
				this.dialog.classList.add(CSS_CLASSES.FALLBACK_OPEN);
			}

			removeFallbackOpenClass() {
				this.dialog.classList.remove(CSS_CLASSES.FALLBACK_OPEN);
			}

			addEventListeners() {
				document.addEventListener('keydown', this.handleKeydown);
				this.dialog.addEventListener('click', this.handleBackdropClick);
			}

			removeEventListeners() {
				document.removeEventListener('keydown', this.handleKeydown);
				this.dialog?.removeEventListener('click', this.handleBackdropClick);
			}

			// Escape differs per presentation: a card yields to whoever claims the key, a modal
			// claims it.
			handleKeydown() {
				throw new Error('handleKeydown() must be implemented');
			}
		}

		// The blocking presentation without a native dialog: takes focus, keeps it, and marks the
		// page behind as gone.
		class FallbackModalDialogStrategy extends FallbackDialogStrategy {
			constructor() {
				super();
				this.handleFocusIn = this.handleFocusIn.bind(this);
			}

			signalArrival() {
				this.focusFirstButtonAfterDelay();
			}

			// Took focus on open, so it owes a return on close wherever focus sits by then.
			restoresFocusUnconditionally() {
				return true;
			}

			hostsAccessibilityDrawer() {
				return true;
			}

			// A modal owns the page beneath, so the escape is claimed at once.
			handleKeydown(event) {
				if (event.key !== KEYS.ESCAPE || !this.mayCloseOnEscape(event)) {
					return;
				}

				event.preventDefault();
				this.popup.close();
			}

			applyAccessibilityAttributes() {
				super.applyAccessibilityAttributes();
				this.dialog.setAttribute('aria-modal', 'true');
			}

			addEventListeners() {
				super.addEventListeners();
				window.addEventListener('focusin', this.handleFocusIn);
			}

			removeEventListeners() {
				super.removeEventListeners();
				window.removeEventListener('focusin', this.handleFocusIn);
			}

			releaseFocusTrap() {
				window.removeEventListener('focusin', this.handleFocusIn);
			}

			handleFocusIn() {
				if (!this.isOpen()) {
					return;
				}

				if (!this.dialog.contains(document.activeElement)) {
					this.returnFocusToPopup();
				}
			}

			// Decided from what is on screen now: a decline with a redirect hides every button after
			// open, and focus() on a hidden button is a no-op that leaves the visitor outside an
			// aria-modal dialog (WCAG 2.1.2, 2.4.3). The dialog's tabindex='-1' is the floor.
			returnFocusToPopup() {
				const content = this.dialog.querySelector(SELECTORS.CONTENT);
				if (content && focusFirstFocusable(content)) {
					return;
				}

				this.dialog.focus();
			}
		}

		// The non-blocking presentation: no aria-modal (the page behind is live), no focus trap (it
		// would strand the keyboard), no focus taken (the live region is the only arrival signal).
		class FallbackCornerCardDialogStrategy extends FallbackDialogStrategy {
			// Never takes focus, so it owes no restore on open.
			show() {
				super.show();
				this.trackFocusOutside();
			}

			// super.close() may focus <body> on an untouched page, so the rescue runs after it.
			close() {
				this.untrackFocusOutside();
				super.close();
				this.returnFocusBesideIfDropped();
			}

			cleanup() {
				this.untrackFocusOutside();
				super.cleanup();
			}

			signalArrival() {
				this.announceArrivalOnce();
			}

			// Shares the page, so it decides after dispatch like the native card.
			handleKeydown(event) {
				this.closeUnlessEscapeClaimed(event);
			}

			// It never held focus; restoring would throw a visitor who has tabbed on back to where
			// they were when it appeared (WCAG 2.4.3).
			restoresFocusUnconditionally() {
				return false;
			}

			hostsAccessibilityDrawer() {
				return false;
			}
		}

		return {
			DialogStrategy,
			ModalDialogStrategy,
			CornerCardDialogStrategy,
			FallbackModalDialogStrategy,
			FallbackCornerCardDialogStrategy,
			focusFirstFocusable,
		};
	})();
